import { getGLBData } from '@proxies/getGLBData'
import { pickLocalizedText } from '@utils/i18n/uiLocale'
import { isPouff } from '@utils/sceneObjects/items/rinconeroUtils'
import { isArmCatalog } from './unitDepthVariants'

export const SEAT_HEIGHT_DEFAULT_CM = 42 as const
export const SEAT_HEIGHT_TALL_CM = 47 as const

export type SeatHeightCm = typeof SEAT_HEIGHT_DEFAULT_CM | typeof SEAT_HEIGHT_TALL_CM

const SEAT_HEIGHT_47_PREFIX_RE = /^47\s+/

export function stripSeatHeightPrefix(catalogID: unknown): {
  baseCatalog: string
  heightCm: SeatHeightCm
} {
  const raw = String(catalogID || '').trim()
  if (!raw) return { baseCatalog: '', heightCm: SEAT_HEIGHT_DEFAULT_CM }
  if (SEAT_HEIGHT_47_PREFIX_RE.test(raw)) {
    return {
      baseCatalog: raw.replace(SEAT_HEIGHT_47_PREFIX_RE, '').trim(),
      heightCm: SEAT_HEIGHT_TALL_CM,
    }
  }
  return { baseCatalog: raw, heightCm: SEAT_HEIGHT_DEFAULT_CM }
}

export function parseSeatHeightFromCatalog(catalogID: unknown): SeatHeightCm {
  return stripSeatHeightPrefix(catalogID).heightCm
}

export function buildCatalogWithSeatHeightCm(
  catalogID: unknown,
  targetHeightCm: number,
): string {
  const { baseCatalog } = stripSeatHeightPrefix(catalogID)
  if (!baseCatalog) return ''
  if (targetHeightCm === SEAT_HEIGHT_TALL_CM) return `47 ${baseCatalog}`
  return baseCatalog
}

export function shouldApplySeatHeightToCatalog(catalogID: unknown): boolean {
  const raw = String(catalogID || '').trim()
  if (!raw || isArmCatalog(raw)) return false
  const upper = raw.toUpperCase()
  if (upper.includes('ASIENTO') || upper.includes('COJIN')) return false
  return true
}

export function resolveDrawCatalogWithSeatHeight(
  catalogID: string,
  heightCm: number,
): string {
  if (!shouldApplySeatHeightToCatalog(catalogID)) return catalogID
  return buildCatalogWithSeatHeightCm(catalogID, heightCm)
}

/** Catálogo base para hijos asiento/cojín (sin prefijo 47). */
export function childBaseCatalogFromUnit(catalogID: unknown): string {
  return stripSeatHeightPrefix(catalogID).baseCatalog
}

function resolveItemCatalogRef(item: any): string {
  return String(item?.catalogID || item?.referencia || '').trim()
}

/** Brazo BR suelto en escena (catálogo o referencia tipo BR1, BR2-100, …). */
export function isSceneArmUnit(item: any): boolean {
  return isArmCatalog(resolveItemCatalogRef(item))
}

export function isBaseSeatParentSceneItem(item: any): boolean {
  if (!item) return false
  if (item.type !== 'linkedItem' && item.type !== 'items') return false
  if (isSceneArmUnit(item)) return false
  const catalog = resolveItemCatalogRef(item).toUpperCase()
  if (catalog.includes('ASIENTO') || catalog.includes('COJIN')) return false
  return true
}

function unitHasSeatAndCushionChildren(unit: any, allItems: any[]): boolean {
  const unitId = String(unit?.id || '')
  if (!unitId) return false

  const childIds = new Set<string>()
  Array.from((unit?.childs as Set<string>) || []).forEach((childId) => childIds.add(String(childId)))
  allItems.forEach((item) => {
    if (item?.type === 'linkedSon' && String(item?.firstParentID || '') === unitId) {
      childIds.add(String(item.id))
    }
  })

  let hasSeat = false
  let hasCushion = false
  childIds.forEach((childId) => {
    const child = allItems.find((item) => String(item?.id) === childId)
    if (!child || child.type !== 'linkedSon') return
    const ref = String(child?.catalogID || child?.referencia || '').toUpperCase()
    if (ref.includes('ASIENTO')) hasSeat = true
    if (ref.includes('COJIN')) hasCushion = true
  })

  return hasSeat && hasCushion
}

/** Unidades que definen altura de cojines: no BR, no puff; prioriza módulos con asiento/cojín. */
export function collectSeatHeightReferenceUnits(
  items: Iterable<unknown> | Map<unknown, any>,
): any[] {
  const values =
    items instanceof Map ? Array.from(items.values()) : Array.from(items as Iterable<any>)
  const baseUnits = values.filter(
    (item) => isBaseSeatParentSceneItem(item) && !isPouff(resolveItemCatalogRef(item)),
  )
  const withSeatCushion = baseUnits.filter((unit) => unitHasSeatAndCushionChildren(unit, values))
  return withSeatCushion.length > 0 ? withSeatCushion : baseUnits
}

export function resolveUnitSeatHeightCm(unit: any): SeatHeightCm {
  const fromCatalog = parseSeatHeightFromCatalog(unit?.catalogID)
  const fromMeta = Number(unit?.seatHeightCm || 0)
  // El prefijo `47` en catálogo manda sobre seatHeightCm:42 heredado al dibujar.
  if (fromCatalog === SEAT_HEIGHT_TALL_CM) return SEAT_HEIGHT_TALL_CM
  if (fromMeta === SEAT_HEIGHT_TALL_CM) return SEAT_HEIGHT_TALL_CM
  if (fromMeta === SEAT_HEIGHT_DEFAULT_CM) return SEAT_HEIGHT_DEFAULT_CM
  return fromCatalog
}

function resolveHeightsFromReferenceUnits(
  items: Iterable<unknown> | Map<unknown, any>,
): SeatHeightCm[] {
  return collectSeatHeightReferenceUnits(items).map(resolveUnitSeatHeightCm)
}

/** Altura de cojines para presupuesto / cabecera (unidades con asiento/cojín, sin BR). */
export function resolveSceneSeatHeightCmForDisplay(
  items: Iterable<unknown> | Map<unknown, any>,
): SeatHeightCm | null {
  const heights = resolveHeightsFromReferenceUnits(items)
  if (heights.length === 0) return null
  if (heights.some((height) => height === SEAT_HEIGHT_TALL_CM)) return SEAT_HEIGHT_TALL_CM
  return SEAT_HEIGHT_DEFAULT_CM
}

/** Escena vacía → siempre 42. Con unidades → hereda altura de módulos con asiento/cojín (sin BR). */
export function getSeatHeightCmForNewUnitsFromScene(
  items: Iterable<unknown> | Map<unknown, any>,
): SeatHeightCm {
  const heights = resolveHeightsFromReferenceUnits(items)
  if (heights.length === 0) return SEAT_HEIGHT_DEFAULT_CM
  if (heights.some((height) => height === SEAT_HEIGHT_TALL_CM)) return SEAT_HEIGHT_TALL_CM
  return heights[0] ?? SEAT_HEIGHT_DEFAULT_CM
}

export function resolveReplaceCatalogWithSeatHeight(
  menuCatalogID: string,
  replacedUnit: any,
): { catalogID: string; seatHeightCm: SeatHeightCm } {
  const seatHeightCm = resolveUnitSeatHeightCm(replacedUnit)
  const catalogID = resolveDrawCatalogWithSeatHeight(menuCatalogID, seatHeightCm)
  return { catalogID, seatHeightCm }
}

export function seatHeightOptionUnavailableMessage(heightCm: number): string {
  return pickLocalizedText({
    es: `Esta unidad no tiene la variante de altura ${heightCm} cm.`,
    en: `This unit does not have the ${heightCm} cm height variant.`,
  })
}

export async function seatHeightVariantExistsInCatalog(catalogID: string): Promise<boolean> {
  const data = await getGLBData(catalogID)
  if (!data) return false
  if (Array.isArray(data)) return data.length > 0

  const referenciaBlender = String((data as { referenciaBlender?: unknown }).referenciaBlender || '').trim()
  return Boolean(referenciaBlender)
}
